import { PlatformLocation } from '@angular/common'
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs/operators'

@Component({
  selector: 'dmmp-content-search',
  templateUrl: './content-search.component.html',
})
export class ContentSearchComponent implements OnInit, OnChanges {
  @Input() module: any
  @Input() result: any
  @Input() identifier: string
  @Input() inSlide: boolean
  @Input() searchName: string
  @Output() clearResult = new EventEmitter()
  @Output() close = new EventEmitter()
  @Output() search = new EventEmitter()
  @Output() schema = new EventEmitter()
  @Output() page = new EventEmitter()
  @Input() paginationState: any
  @Input() linkResultSearch = '/resultado-busqueda'
  @Input() currentFilters: any
  @Input() contentResult: any
  @Input() loaderState: boolean
  @Input() currentSearchUpdate: any
  @Input() relateUrl: string = '/filtros-programas'
  isSearching: boolean = false
  searchGroup: FormGroup
  searchTitle = 'Búsquedas relacionadas'
  resultEntriesLabel = 'entradas encontradas'

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private location: PlatformLocation,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnChanges(simpleChange: SimpleChanges) {
    if (simpleChange?.currentSearchUpdate?.currentValue === '') {
      this.searchGroup?.reset()
    }
  }

  ngOnInit(): void {
    if (
      this.activatedRoute.snapshot.queryParams.q &&
      this.location.pathname !== this.relateUrl
    ) {
      this.searchGroup = this.formBuilder.group({
        search: [
          this.activatedRoute.snapshot.queryParams.q,
          [Validators.required, Validators.minLength(3)],
        ],
      })
    } else if (
      this.activatedRoute.snapshot.queryParams.q &&
      this.location.pathname === this.relateUrl
    ) {
      this.searchGroup = this.formBuilder.group({
        search: [
          this.activatedRoute.snapshot.queryParams.q,
          [Validators.required, Validators.minLength(3)],
        ],
      })
      this.reactiveInput()
    } else {
      this.searchGroup = this.formBuilder.group({
        search: [''],
      })
      this.reactiveInput()
    }
  }

  onSubmit(): void {
    const searchContentIndex = this.module.search_content_index.length
    if (this.searchGroup.value['search'] === '') {
      this.router.navigate([], {
        queryParams: { q: null },
        queryParamsHandling: 'merge',
      })
      this.search.emit({
        searchString: null,
        from: 0,
        size: this.paginationState.quantityForPages,
      })
      if (searchContentIndex > 1) {
        this.router.navigate([this.linkResultSearch], {
          queryParams: { q: this.searchGroup.value['search'] },
        })
        this.page.emit()
      }
    } else {
      this.makeQueryString()
    }
  }

  makeQueryString(): void {
    let contentTypeQuery = ''
    const searchContentIndex = this.module.search_content_index.length
    if (searchContentIndex > 1) {
      this.isSearching = true
    }
    if (searchContentIndex > 0) {
      contentTypeQuery = 'AND('
      this.module.search_content_index.forEach((indexType) => {
        contentTypeQuery += `(content_type:(${indexType}))OR`
      })
      contentTypeQuery =
        contentTypeQuery.substring(0, contentTypeQuery.length - 2) + ')'
    }

    const searchInput = this.searchGroup.value['search']
      ? this.searchGroup.value['search'].length < 3
        ? ''
        : `(title:(${this.searchGroup.value['search']}))${contentTypeQuery}`
      : ''

    if (searchContentIndex === 1) {
      this.search.emit({
        searchString: `${searchInput}`,
        from: 0,
        size: this.paginationState.quantityForPages,
      })
    } else {
      this.search.emit({
        searchString: `${searchInput}`,
        from: 0,
        size: 10,
      })
    }
    if (searchInput === '') {
      this.isSearching = false
    }
  }

  reactiveInput() {
    const searchContentIndex = this.module.search_content_index.length
    if (
      this.module.search_type === 'reactive_search' ||
      (this.module.search_type === 'redirect_search' && searchContentIndex > 1)
    ) {
      this.searchGroup.controls.search.valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          filter(
            (valueSearch) =>
              valueSearch?.length >= 3 || valueSearch?.length === 0
          ),
          tap(() => {
            this.onSubmit()
          })
        )
        .subscribe()
    } else if (this.location.pathname === this.relateUrl) {
      this.searchGroup.controls.search.valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          filter((valueSearch) => valueSearch?.length === 0),
          tap(() => {
            this.onSubmit()
          })
        )
        .subscribe()
    }
  }

  redirectToPage() {
    const searchContentIndex = this.module.search_content_index.length
    if (
      searchContentIndex === 1 &&
      this.module.search_content_index.includes('programs') &&
      this.location.pathname !== this.relateUrl
    ) {
      this.router.navigate([this.relateUrl], {
        queryParams: { q: this.searchGroup.value['search'] },
      })
      this.page.emit()
    } else if (
      searchContentIndex === 1 &&
      this.module.search_content_index.includes('programs') &&
      this.location.pathname === this.relateUrl
    ) {
      this.router.navigate([this.relateUrl], {
        queryParams: { q: this.searchGroup.value['search'] },
      })
      this.onSubmit()
    } else if (searchContentIndex > 1) {
      this.router.navigate([this.linkResultSearch], {
        queryParams: { q: this.searchGroup.value['search'] },
      })
      this.page.emit()
    }
  }

  closeSearch() {
    this.searchGroup.reset()
    this.onSubmit()
    this.close.emit()
  }

  resetSearch() {
    this.isSearching = false
    this.searchGroup.reset()
    this.search.emit({ searchString: '', from: 0, size: 10 })
  }
}
